import { useMemo } from 'react'
import { useCohortsByDistrict } from '@/hooks/useCohortsByDistrict'
import { CohortCard } from './CohortCard'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { cn } from '@/lib/utils'

interface CohortListProps {
  districtId: string
  districtName?: string
  className?: string
}

function formatPopulation(population: number): string {
  return population.toLocaleString()
}

function formatTurnout(turnout: number): string {
  return `${Math.round(turnout * 100)}%`
}

/**
 * Component to display all cohorts in a district with summary statistics.
 */
export function CohortList({ districtId, districtName, className }: CohortListProps) {
  const { cohorts, isLoading, error } = useCohortsByDistrict(districtId)

  const stats = useMemo(() => {
    if (!cohorts || cohorts.length === 0) {
      return {
        totalPopulation: 0,
        cohortCount: 0,
        averageTurnout: 0,
      }
    }

    const totalPopulation = cohorts.reduce((sum, c) => sum + c.population, 0)
    const weightedTurnoutSum = cohorts.reduce((sum, c) => sum + c.base_turnout * c.population, 0)
    const averageTurnout = totalPopulation > 0 ? weightedTurnoutSum / totalPopulation : 0

    return {
      totalPopulation,
      cohortCount: cohorts.length,
      averageTurnout,
    }
  }, [cohorts])

  if (error) {
    return (
      <div className={cn('rounded-lg border border-red-200 bg-red-50 p-4 text-red-800', className)}>
        Failed to load cohorts. Please try again later.
      </div>
    )
  }

  if (isLoading) {
    return <CohortListSkeleton className={className} />
  }

  if (!cohorts || cohorts.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-lg">
            Cohorts{districtName && ` in ${districtName}`}
          </CardTitle>
          <CardDescription>No cohorts have been generated yet</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
            <p>This district has no voter cohorts.</p>
            <p className="text-sm mt-1">Generate cohorts to see the electorate breakdown.</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Summary Stats Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            Cohorts{districtName && ` in ${districtName}`}
          </CardTitle>
          <CardDescription>
            Demographic groups that make up the district's electorate
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-6 text-sm">
            <div>
              <span className="text-muted-foreground">Total Population:</span>{' '}
              <span className="font-semibold">{formatPopulation(stats.totalPopulation)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Cohorts:</span>{' '}
              <span className="font-semibold">{stats.cohortCount}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Avg Turnout:</span>{' '}
              <span className="font-semibold">{formatTurnout(stats.averageTurnout)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cohort Cards */}
      <div className="space-y-3">
        {cohorts.map((cohort) => (
          <CohortCard key={cohort.id} cohort={cohort} />
        ))}
      </div>
    </div>
  )
}

function CohortListSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('space-y-4', className)}>
      <Card>
        <CardHeader className="pb-3">
          <div className="h-5 w-48 bg-muted animate-pulse rounded" />
          <div className="h-4 w-64 bg-muted animate-pulse rounded mt-2" />
        </CardHeader>
        <CardContent>
          <div className="flex gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-4 w-32 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
      <div className="space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <CohortCardSkeleton key={i} />
        ))}
      </div>
    </div>
  )
}

function CohortCardSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-2">
            <div className="h-5 w-56 bg-muted animate-pulse rounded" />
            <div className="h-4 w-40 bg-muted animate-pulse rounded" />
          </div>
          <div className="h-8 w-16 bg-muted animate-pulse rounded" />
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="flex items-center justify-between gap-4">
          <div className="h-4 w-24 bg-muted animate-pulse rounded" />
          <div className="h-4 w-32 bg-muted animate-pulse rounded" />
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Export for use in other components
 */
export { CohortCardSkeleton, CohortListSkeleton }
